import { useEffect } from "react";
import { Autocomplete, Grid, MenuItem, Stack, Typography } from "@mui/material";
import { Label } from "./SelectAccountType";
import { StyledErrorField, StyledTextField } from "./styled-components/AddressFields";
import { Field, useFormikContext } from "formik";
import { StyledEndAdorment } from "./AddressLookup";
import ArrowDown from "src/icons/ArrowDown";
import { FormValues } from "./utils/formSchema";
import { useAppDispatch } from "src/store/store";
import {
  WealthSourceOptions,
  setFrequency,
  setHearAboutUs,
  setIndustry,
  setOccupation,
  setOtherWealthSource,
  setReduxCryptoExperience,
  setReduxQuoteCurrency,
  setReduxSourceOfFunds,
  setReferralCode,
  setPortfolioValue,
  useOnboarding,
  setAccountPurpose,
  AccountPurposeOptions,
} from "src/store/features/onboarding.slice";
import StyledInvestmentSlider from "./StyledInvestmentSlider";
import { batch } from "react-redux";
import {
  accountPurposeOptions,
  experienceOptions,
  frequencyOptions,
  referralOptions,
  supportedCurrencies,
  wealthSourceOptions,
} from "./utils/pickListOptions";
import api from "src/store/features/api";

const CryptoJourney = () => {
  const { setFieldValue, setFieldError } = useFormikContext();
  const { prefilledInvestmentExp } = useOnboarding();

  const dispatch = useAppDispatch();

  const {
    values: {
      frequency,
      occupation,
      industry,
      hearAboutUs,
      otherWealthSource,
      defaultCurrency,
      investmentExp,
      wealthSource,
      referralCode,
      portfolioValue,
      accountPurpose,
    },
    errors: {
      frequency: frequencyError,
      occupation: occupationError,
      industry: industryError,
      hearAboutUs: hearAboutUsError,
      defaultCurrency: defaultCurrencyError,
      investmentExp: investmentExpError,
      wealthSource: wealthSourceError,
      accountPurpose: accountPurposeError,
      referralCode: referralCodeError,
      country: countryError,
      state: stateError,
    },
  } = useFormikContext<FormValues>();

  useEffect(() => {
    // Update context so data is available when navigated back & forth
    // This will result in a single re-render, no matter how many actions are dispatched within the batch function.
    batch(() => {
      dispatch(setReduxSourceOfFunds(wealthSource));
      dispatch(setReduxCryptoExperience(investmentExp));
      dispatch(setReduxQuoteCurrency(defaultCurrency));
      dispatch(setOccupation(occupation));
      dispatch(setIndustry(industry));
      dispatch(setFrequency(frequency));
      dispatch(setHearAboutUs(hearAboutUs));
      dispatch(setOtherWealthSource(otherWealthSource));
      dispatch(setReferralCode(referralCode));
      dispatch(setPortfolioValue(portfolioValue));
      dispatch(setAccountPurpose(accountPurpose));
    });
  }, [
    wealthSource,
    investmentExp,
    defaultCurrency,
    occupation,
    industry,
    frequency,
    hearAboutUs,
    otherWealthSource,
    referralCode,
    portfolioValue,
    accountPurpose,
  ]);

  const handleMultiSelect = (option: WealthSourceOptions) => {
    // Check if wealthSource already includes the selected option.
    if (wealthSource.includes(option)) {
      // If it does, filter out the selected option from the array.
      const newWealthSource = wealthSource.filter((item) => item !== option);
      setFieldValue("wealthSource", newWealthSource);
    } else {
      // If it doesn't, push the selected option to the array.
      setFieldValue("wealthSource", [...wealthSource, option]);
    }
  };

  const handleAccountPurposeMultiSelect = (option: AccountPurposeOptions) => {
    // same as above in terms of logic. remove the option if it's already selected, otherwise add it.
    if (accountPurpose.includes(option)) {
      const newAccountPurpose = accountPurpose.filter((item) => item !== option);
      setFieldValue("accountPurpose", newAccountPurpose);
    } else {
      setFieldValue("accountPurpose", [...accountPurpose, option]);
    }
  };

  const sourceOfFundsOtherSelected = wealthSource.find((item) => item === "Others");
  const hearAboutUsRaFSelected = hearAboutUs === "Referral Partner / Affiliate";
  const hearAboutUsRequiresChannel =
    hearAboutUs === "Online - social media" || hearAboutUs === "Advertisement";

  const { data: occupationList } = api.useGetOccupationListQuery();
  const { data: industriesList } = api.useGetIndustriesListQuery();

  return (
    <div>
      <Grid container spacing={1}>
        {(countryError || stateError) && (
          <Grid item xs={12}>
            <Typography color="error" variant="body2" sx={{ mb: 1 }}>
              Your country or state of residence cannot be submitted as entered. Use{" "}
              <strong>Back</strong> to return to your details and update your address.
            </Typography>
            {countryError && <StyledErrorField>{countryError}</StyledErrorField>}
            {stateError && (
              <StyledErrorField style={{ display: "block", marginTop: countryError ? 8 : 0 }}>
                {stateError}
              </StyledErrorField>
            )}
          </Grid>
        )}
        <Grid item xs={12} mt={2} mb={1}>
          <Label text="Which of the following are sources of the funds that you will be using with Caleb & Brown?" />
        </Grid>
        {wealthSourceOptions.map((option) => {
          return (
            <Grid
              item
              xs={6}
              sm={4}
              md={3}
              lg={2.2}
              onClick={() => handleMultiSelect(option.value)}
              data-testid={`wealth-source-${option.value}`}
              key={option.value}>
              <StyledTextField
                value={option.label}
                custombgcolour={"#F0FEF9"}
                selected={wealthSource.includes(option.value)}
                name="wealthSource"
                error={!!wealthSourceError}
                style={{ pointerEvents: "none" }}
                InputProps={{
                  endAdornment: (
                    <StyledEndAdorment selected={wealthSource.includes(option.value)} />
                  ),
                }}
              />
            </Grid>
          );
        })}
        {sourceOfFundsOtherSelected && (
          <Grid item xs={12}>
            <Field
              as={StyledTextField}
              name={"otherWealthSource"}
              label={"Enter Source of Funds here"}
              selected={!!sourceOfFundsOtherSelected}
              InputProps={{
                endAdornment: <StyledEndAdorment selected={otherWealthSource !== ""} />,
              }}
            />
          </Grid>
        )}
        {/* wealthSourceError error */}
        <Grid item xs={12}>
          {wealthSourceError && <StyledErrorField>{wealthSourceError}</StyledErrorField>}
        </Grid>
        <Grid item xs={12} mb={1} display={prefilledInvestmentExp ? "none" : "flex"}>
          <Label text="How long have you been trading Crypto? Select one:" />
        </Grid>
        {experienceOptions.map((option) => {
          return (
            <Grid
              item
              xs={6}
              sm={4}
              md={3}
              lg={2}
              onClick={() => setFieldValue("investmentExp", option.value)}
              key={option.value}
              display={prefilledInvestmentExp ? "none" : "flex"}>
              <StyledTextField
                value={option.label}
                custombgcolour={"#F0FEF9"}
                name="investmentExp"
                selected={investmentExp?.includes(option.value)}
                error={!!investmentExpError}
                style={{ pointerEvents: "none" }}
                InputProps={{
                  endAdornment: (
                    <StyledEndAdorment selected={investmentExp?.includes(option.value)} />
                  ),
                }}
              />
            </Grid>
          );
        })}
        {/* investmentExp error */}
        <Grid item xs={12}>
          {investmentExpError && <StyledErrorField>{investmentExpError}</StyledErrorField>}
        </Grid>
        <Grid item xs={12} mb={1}>
          <Label text="What is your preferred currency?" />
        </Grid>
        {supportedCurrencies.map((option) => {
          return (
            <Grid
              item
              xs={6}
              sm={4}
              md={2}
              key={option.label}
              data-testid={`default-currency-${option.label}`}
              onClick={() => setFieldValue("defaultCurrency", option.value)}>
              <StyledTextField
                value={option.label}
                name="defaultCurrency"
                custombgcolour={"#F0FEF9"}
                selected={defaultCurrency.includes(option.value)}
                error={!!defaultCurrencyError}
                style={{ pointerEvents: "none" }}
                InputProps={{
                  endAdornment: (
                    <StyledEndAdorment selected={defaultCurrency.includes(option.value)} />
                  ),
                }}
              />
            </Grid>
          );
        })}
        {/* currency error */}
        <Grid item xs={12}>
          {defaultCurrencyError && <StyledErrorField>{defaultCurrencyError}</StyledErrorField>}
        </Grid>
        {/* OCCUPATION */}
        <Grid item xs={12} lg={12} mb={1}>
          <Stack spacing={2}>
            <Label text="What is your occupation?" />
            {/* Occupation */}
            <Autocomplete
              options={occupationList ?? []}
              value={occupation}
              onBlur={() => {
                // Handles click away with invalid option selected
                if (occupationList?.includes(occupation)) {
                  setFieldError("occupation", "");
                } else {
                  setFieldError("occupation", "Invalid occupation selected");
                }
              }}
              onInputChange={(event, newValue) => {
                if (newValue && occupationList?.includes(newValue)) {
                  setFieldValue("occupation", newValue);
                } else {
                  setFieldError("occupation", "Invalid occupation selected");
                }
              }}
              renderInput={(params) => (
                <Field
                  {...params}
                  as={StyledTextField}
                  selected={!!occupation && !occupationError}
                  label="Enter your occupation"
                  name="occupation"
                  error={!!occupationError}
                  helperText={occupationError}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: <StyledEndAdorment selected={!!occupation && !occupationError} />,
                  }}
                />
              )}
            />
          </Stack>
        </Grid>
        {/* INDUSTRY */}
        <Grid item xs={12} lg={12} mb={1}>
          <Stack spacing={2}>
            {/* Industries */}
            <Label text="What is your industry?" />
            <Autocomplete
              options={industriesList ?? []}
              value={industry}
              onBlur={() => {
                // Handles click away with invalid option selected
                if (industriesList?.includes(industry)) {
                  setFieldError("industry", "");
                } else {
                  setFieldError("industry", "Invalid occupation selected");
                }
              }}
              onInputChange={(event, newValue) => {
                if (newValue && industriesList?.includes(newValue)) {
                  setFieldValue("industry", newValue);
                } else {
                  setFieldError("industry", "Invalid industry selected");
                }
              }}
              renderInput={(params) => (
                <Field
                  {...params}
                  data-testid="industry-field"
                  as={StyledTextField}
                  selected={!!industry && !industryError}
                  label="Enter your industry"
                  name="industry"
                  error={!!industryError}
                  helperText={industryError}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: <StyledEndAdorment selected={!!industry && !industryError} />,
                  }}
                />
              )}
            />
          </Stack>
        </Grid>
        {/* INITIAL INVESTMENT & FREQUENCY */}
        <Grid
          container
          item
          spacing={"24px"}
          xs={12}
          mb={1}
          display="flex"
          alignItems={"flex-start"}
          alignSelf={"stretch"}>
          {/* INITIAL INVESTMENT */}
          <Grid
            item
            xs={12}
            md={6}
            display="flex"
            flexDirection="column"
            alignItems="flex-start"
            justifyContent={{ xs: "flex-start", md: "space-between" }}
            gap="16px"
            height={{ xs: "auto", md: "100%" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "16px", width: "100%" }}>
              <Label text="How much do you plan to trade?" />
              <div
                style={{
                  color: "#7B858B",
                  fontFamily: "Söhne",
                  fontSize: "12px",
                  fontStyle: "normal",
                  fontWeight: "400",
                  lineHeight: "14px",
                  alignSelf: "stretch",
                }}>
                Trade minimums: $500 through client Portal | $2000 through email to your broker
              </div>
            </div>
            <StyledInvestmentSlider type="investment" />
          </Grid>
          {/* FREQUENCY */}
          <Grid
            item
            xs={12}
            md={6}
            display="flex"
            flexDirection="column"
            alignItems="flex-start"
            justifyContent={{ xs: "flex-start", md: "space-between" }}
            gap="16px"
            height={{ xs: "auto", md: "100%" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "16px", width: "100%" }}>
              <Label text="How frequently do you plan to trade this amount with us?" />
            </div>
            <Field
              as={StyledTextField}
              label={"Select an item"}
              error={!!frequencyError}
              helperText={frequencyError}
              select
              selected={frequency !== ""}
              variant={"outlined"}
              name={"frequency"}
              SelectProps={{
                IconComponent: () => (
                  <div style={{ display: true ? "none" : "flex" }}>
                    <ArrowDown />
                  </div>
                ),
              }}
              InputProps={{
                endAdornment: <StyledEndAdorment selected={frequency !== ""} />,
              }}>
              {frequencyOptions.map((item, it) => (
                <MenuItem key={item + it} value={item}>
                  <Typography sx={{ textTransform: "capitalize" }}>{item}</Typography>
                </MenuItem>
              ))}
              <MenuItem sx={{ display: "none" }} value=""></MenuItem>
            </Field>
          </Grid>
        </Grid>
        {/* PORTFOLIO VALUE & ACCOUNT PURPOSE */}
        <Grid
          container
          item
          spacing={"24px"}
          xs={12}
          mb={1}
          display="flex"
          alignItems={"flex-start"}
          alignSelf={"stretch"}>
          {/* PORTFOLIO VALUE */}
          <Grid
            item
            xs={12}
            md={6}
            display="flex"
            flexDirection="column"
            alignItems="flex-start"
            justifyContent={{ xs: "flex-start", md: "space-between" }}
            gap="16px"
            height={{ xs: "auto", md: "100%" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "16px", width: "100%" }}>
              <Label text="What is the current value of your existing cryptocurrency holdings?" />
              <ul
                style={{
                  color: "#7B858B",
                  fontFamily: "Söhne",
                  fontSize: "12px",
                  fontStyle: "normal",
                  fontWeight: "400",
                  lineHeight: "14px",
                  paddingLeft: "14px",
                  margin: 0,
                }}>
                <li>
                  This is the combined total of all of your crypto holdings across all exchanges and
                  wallets
                </li>
                <li>This will help us to provide personalised service based on your holdings</li>
              </ul>
            </div>
            <StyledInvestmentSlider type="portfolio" />
          </Grid>
          {/* ACCOUNT PURPOSE */}
          <Grid
            item
            xs={12}
            md={6}
            display="flex"
            flexDirection="column"
            alignItems="flex-start"
            justifyContent={{ xs: "flex-start", md: "space-between" }}
            gap="16px"
            height={{ xs: "auto", md: "100%" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "16px", width: "100%" }}>
              <Label text="What is the purpose of your account with Caleb & Brown?" />
            </div>
            <Field
              as={StyledTextField}
              select
              multiple
              value={accountPurpose ?? []}
              onChange={(event: any) => {
                const value = event.target.value;
                setFieldValue(
                  "accountPurpose",
                  typeof value === "string" ? value.split(",") : value,
                );
              }}
              selected={!!accountPurpose && accountPurpose.length > 0}
              label="Select account purpose(s)"
              name="accountPurpose"
              error={!!accountPurposeError}
              helperText={accountPurposeError}
              fullWidth
              SelectProps={{
                multiple: true,
                IconComponent: () => null,
                renderValue: (selected: any) => {
                  const displayText = (selected as string[]).join(", ");
                  return (
                    <span
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: "85%",
                      }}>
                      {displayText}
                    </span>
                  );
                },
              }}
              InputProps={{
                endAdornment: (
                  <StyledEndAdorment selected={!!accountPurpose && accountPurpose.length > 0} />
                ),
              }}>
              {accountPurposeOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  <Typography sx={{ textTransform: "capitalize" }}>{option}</Typography>
                </MenuItem>
              ))}
            </Field>
          </Grid>
        </Grid>
        {/* HEAR ABOUT US */}
        <Grid item xs={12} mb={1}>
          <Label text="How did you hear about us?" />
        </Grid>
        <Grid item xs={12} mb={2}>
          <Field
            as={StyledTextField}
            label={"Select an item"}
            select
            selected={!!hearAboutUs}
            error={!!hearAboutUsError}
            helperText={hearAboutUsError}
            variant={"outlined"}
            name={"hearAboutUs"}
            SelectProps={{
              IconComponent: () => (
                <div style={{ display: true ? "none" : "flex" }}>
                  <ArrowDown />
                </div>
              ),
            }}
            InputProps={{
              endAdornment: <StyledEndAdorment selected={!!hearAboutUs} />,
            }}>
            {referralOptions.map((item, it) => (
              <MenuItem key={item + it} value={item}>
                <Typography sx={{ textTransform: "capitalize" }}>{item}</Typography>
              </MenuItem>
            ))}
            <MenuItem sx={{ display: "none" }} value=""></MenuItem>
          </Field>
        </Grid>
        {hearAboutUsRaFSelected && (
          <Grid item xs={12}>
            <Field
              as={StyledTextField}
              name={"referralCode"}
              label={"Enter your referral partner's name or code here"}
              selected={!!referralCode && !referralCodeError}
              error={!!referralCodeError}
              helperText={referralCodeError}
              InputProps={{
                endAdornment: <StyledEndAdorment selected={!!referralCode && !referralCodeError} />,
              }}
            />
          </Grid>
        )}
        {hearAboutUsRequiresChannel && (
          <>
            <Grid item xs={12} mb={1}>
              <Label text="Was it a referral partner / affiliate? If so, what is their name? If not, which social media channel?" />
            </Grid>
            <Grid item xs={12}>
              <Field
                as={StyledTextField}
                name={"referralCode"}
                label={"Enter text here"}
                selected={!!referralCode && !referralCodeError}
                error={!!referralCodeError}
                helperText={referralCodeError}
                InputProps={{
                  endAdornment: (
                    <StyledEndAdorment selected={!!referralCode && !referralCodeError} />
                  ),
                }}
              />
            </Grid>
          </>
        )}
      </Grid>
    </div>
  );
};

export default CryptoJourney;
